// =====================================================
// S-I18N-W1a — the i18next runtime (ADR-A1).
//
// react-i18next + i18next-icu. ICU is not optional: Arabic has SIX plural
// forms (zero/one/two/few/many/other) and any `count === 1 ? a : b` shape
// is wrong for it from day one, in number ranges QA does not routinely hit.
//
// Namespaces are loaded LAZILY, one small JSON per (locale, module), through
// `import.meta.glob`. Everything is self-hosted — no CDN, no i18next-http-
// backend fetching from anywhere (hard rule 5).
//
// The shell boots with `common` + `shell` awaited (they are on every screen,
// so a lazy load there would flash raw keys); a page namespace arrives with
// the page's own chunk.
// =====================================================

import i18n, { type BackendModule, type ReadCallback, type Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import { intlLocale } from "./intl";
import { prepareLocaleFormatters } from "./intl-fallback";
import { registryICU } from "./registry-icu";
import { localeMetadata } from "./locale-registry";
import catalogueLoaders from "virtual:i18n-catalogues";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  getLocale,
  normaliseLocale,
  setLocale,
  type AppLocale,
} from "./locale-state";

/** Namespaces the app shell cannot render without. */
export const BOOT_NAMESPACES = ["common", "shell"] as const;

/** One ICU numbering contract, also used by isolated non-exposure fixtures. */
export const REGISTRY_ICU_OPTIONS = {
  // A lazy target bundle must invalidate ICU messages cached from fa fallback.
  bindI18nStore: "added removed",
  parseLngForICU: (lng: string) => {
    const metadata = localeMetadata(lng);
    return metadata ? intlLocale(metadata.languageTag) : lng;
  },
};

/**
 * The build supplies a lazy namespace lookup per registered locale, then one
 * lazy JSON importer per namespace. Unselected locales' lookup tables are not
 * downloaded at boot. Content and provenance/exposure contracts are unchanged.
 */
const keyOf = (language: string, namespace: string): string => `../locales/${language}/${namespace}.json`;

/** i18next backend over locale maps. Absent file ⇒ existing fallback policy. */
const globBackend: BackendModule = {
  type: "backend",
  init: () => {
    /* nothing to configure — the glob map is static */
  },
  read: (language: string, namespace: string, callback: ReadCallback) => {
    const loadLocale = catalogueLoaders[language];
    if (!loadLocale) {
      // A namespace that has no file for this locale falls back to `fa`
      // through i18next's own fallback chain rather than blowing up the
      // page. `i18n-key-parity` in the deploy gate is what actually makes
      // this case impossible in a shipped build.
      callback(null, {});
      return;
    }
    loadLocale()
      .then(({ default: namespaces }) => namespaces[keyOf(language, namespace)]?.() ?? {})
      .then((mod) => callback(null, ((mod as { default?: Resource }).default ?? mod) as Resource))
      .catch((err) => callback(err as Error, false));
  },
};

let started: Promise<typeof i18n> | null = null;
const synchroniseFormatLocale = (next: string): void => { setLocale(next); };

/**
 * Boot the runtime for `locale` and await the shell namespaces. Idempotent:
 * a second call returns the same promise (React StrictMode double-invokes
 * effects in development).
 */
export const initI18n = (
  locale: AppLocale = DEFAULT_LOCALE,
  /**
   * namespaceهایی که علاوه بر شل باید *پیش از اولین رندر* حاضر باشند —
   * کاتالوگِ همان مسیر (i18n/route-namespaces.ts). موازی با شل درخواست
   * می‌شوند، پس رفت‌وبرگشتِ تازه‌ای اضافه نمی‌کنند و در عوض کلیدِ خام روی
   * صفحه نمی‌آید.
   */
  extraNamespaces: readonly string[] = [],
): Promise<typeof i18n> => {
  if (started) return started;
  setLocale(locale);
  // S-FIX-BUNDLE-4 / R2-115 — قفلِ اتمیِ «رشته و رقم»، اندازه‌گیری‌شده نه حدسی.
  //
  // `changeLocale` پایین سلولِ قالب‌بندی را *پس از* `changeLanguage` حرکت
  // می‌دهد — و آن ترتیب هنوز درست است. اما i18next رویدادِ `languageChanged`
  // را *پیش از* resolve شدنِ همان promise می‌زند، و `useTranslation` دقیقاً
  // روی همین رویداد رندرِ تازه را زمان‌بندی می‌کند. پس رندری که رشته‌های تازه
  // را چاپ می‌کند، هنوز ارقامِ زبانِ قبلی را می‌بیند و چون بعد از آن هیچ
  // رندرِ دیگری لازم نیست، همان‌طور می‌ماند.
  // `tests/e2e/i18n-r2-115-grades-ar.spec.js` این شکاف را در هر سه مرورگر
  // دید: در بازگشتِ ar→fa روی `/my-grades` متن فارسی می‌شد ولی سقفِ نمره
  // «از ٢٠» عربی می‌ماند — شکستِ قاعدهٔ ۲ در یک رفت‌وبرگشت.
  //
  // شنوندهٔ زیر پیش از init ثبت می‌شود، پس از هر شنوندهٔ react-i18next زودتر
  // است (EventEmitterِ i18next به ترتیبِ ثبت صدا می‌زند)؛ تا لحظهٔ رندرِ تازه
  // سلول هم‌رفته است. این یک افزودنی است نه جایگزینی: `changeLocale` و
  // `bootstrap.ts` هر دو هنوز `setLocale` را صدا می‌زنند.
  i18n.off("languageChanged", synchroniseFormatLocale);
  i18n.on("languageChanged", synchroniseFormatLocale);
  started = prepareLocaleFormatters(intlLocale(locale)).then(() => new Promise<typeof i18n>((resolve, reject) => {
    i18n
    .use(globBackend)
    // ICU numbers/plural # follow the shared registry numbering contract.
    .use(registryICU(REGISTRY_ICU_OPTIONS))
    .use(initReactI18next)
    .init({
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      // Script-bearing tags such as zh-Hans / pa-Guru must keep their full
      // identity. normaliseLocale already maps regional requests (fa-IR,
      // ar-SA) onto an exposed canonical pack before i18next sees them.
      load: "currentOnly",
      defaultNS: "common",
      ns: [...new Set([...BOOT_NAMESPACES, ...extraNamespaces])],
      // ICU owns placeholder substitution; i18next's own interpolation is
      // off so `{count}` is never double-processed.
      interpolation: { escapeValue: false },
      // No Suspense: the shell namespaces are awaited here, and a page
      // namespace arriving late re-renders through the `ready` flag instead
      // of unmounting the surrounding chrome.
      react: { useSuspense: false },
      returnNull: false,
    }, (error) => {
      // i18next's init Promise resolves even when its callback reports a
      // backend failure; first paint must observe that real failure.
      if (error) reject(error);
      else resolve(i18n);
    });
  }));
  return started;
};

/**
 * Switch language at runtime: i18next reloads the mounted namespaces, and the
 * formatting cell moves only once they are in memory, so strings and numbers
 * can never be a frame apart.
 *
 * The order matters and was measured, not assumed. The cell used to move
 * first, on the theory that both happened "in the same tick" -- they do not.
 * A locale's catalogue is a dynamic `import()` (see the locale backend above),
 * so `changeLanguage` resolves a tick or more later; in that gap `format.js`
 * and `jalali.ts` were already printing Arabic-Indic digits while every
 * mounted component still held the Persian strings. Nothing outside i18next
 * learns about the new locale until the new strings actually exist.
 */
export const changeLocale = async (next: AppLocale): Promise<AppLocale> => {
  // Clamp before either runtime sees it: an unsupported value must leave both
  // where they are, not switch i18next to a language with no catalogue.
  const applied = normaliseLocale(next) ?? getLocale();
  await prepareLocaleFormatters(intlLocale(applied));
  if (i18n.isInitialized && i18n.language !== applied) {
    // i18next's changeLanguage promise can resolve even when the backend
    // reports a load error. Preload first, without changing language/state or
    // the persisted preference, and reject an incomplete target bundle.
    await new Promise<void>((resolve, reject) => {
      i18n.loadLanguages(applied, (error) => error ? reject(error) : resolve());
    });
    const { default: sourceNamespaces } = await catalogueLoaders[DEFAULT_LOCALE]();
    const namespaces = typeof i18n.options.ns === "string" ? [i18n.options.ns] : i18n.options.ns ?? [];
    for (const namespace of new Set([...BOOT_NAMESPACES, ...namespaces])) {
      if (sourceNamespaces[keyOf(DEFAULT_LOCALE, namespace)] && !i18n.hasResourceBundle(applied, namespace)) {
        throw new Error(`Locale catalogue failed to load: ${applied}/${namespace}`);
      }
    }
    await i18n.changeLanguage(applied);
  }
  return setLocale(applied);
};

/** Load one page namespace on demand. Safe to call repeatedly. */
export const loadNamespace = async (namespace: string): Promise<void> => {
  if (!i18n.isInitialized) return;
  await i18n.loadNamespaces(namespace);
};

/** Test seam — drops the memoised init so each test file boots clean. */
export const __resetI18nForTests = (): void => {
  started = null;
};

export { getLocale };
export default i18n;
